import React from 'react';
import { NumberInput as ArkNumberInput } from '@ark-ui/react/number-input';
import clsx from 'clsx';
import { Icon } from '../../../components/Icon/Icon';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface NumberInputProps extends BaseInputProps<number | undefined> {
  /** Minimální povolená hodnota. */
  min?: number;
  /** Maximální povolená hodnota. */
  max?: number;
  /** Krok inkrementace/dekrementace. */
  step?: number;
  /** Lokalizované popisky spin tlačítek (zag default je anglický). */
  incrementLabel?: string;
  decrementLabel?: string;
  className?: string;
}

/**
 * Atomický číselný input postavený na Ark UI `NumberInput`.
 *
 * RHF pracuje s `number`em; Ark interně přenáší `string`. Prázdná hodnota se
 * mapuje na `undefined`, jinak na `valueAsNumber`.
 *
 * `id` z `<FormField />` se mapuje přes `ids.input` (ne přímým přepsáním `id`
 * na `<Input>`) — zag si vnitřní elementy hledá přes `getElementById`, takže
 * přepsaný `id` by rozbil synchronizaci zobrazené a skutečné hodnoty.
 */
export const NumberInput: React.FC<NumberInputProps> = ({
  name,
  id,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  readOnly,
  hasError,
  placeholder,
  describedBy,
  testAttributes,
  min,
  max,
  step,
  incrementLabel = 'Zvýšit hodnotu',
  decrementLabel = 'Snížit hodnotu',
  className,
}) => (
  <ArkNumberInput.Root
    name={name}
    ids={{ input: id }}
    value={value === undefined || value === null ? '' : String(value)}
    disabled={disabled}
    readOnly={readOnly}
    invalid={hasError}
    min={min}
    max={max}
    step={step}
    translations={{
      incrementLabel,
      decrementLabel,
    }}
    onValueChange={(details) =>
      onChange(Number.isNaN(details.valueAsNumber) ? undefined : details.valueAsNumber)
    }
    className={clsx(styles.numberRoot, className)}
  >
    <ArkNumberInput.Control className={styles.numberControl}>
      <ArkNumberInput.Input
        ref={ref}
        placeholder={placeholder}
        onBlur={onBlur}
        className={styles.numberInput}
        aria-describedby={describedBy}
        {...testAttributes}
      />
      <div className={styles.numberTriggers}>
        <ArkNumberInput.IncrementTrigger className={styles.numberTrigger}>
          <Icon name="chevron-up" size={16} />
        </ArkNumberInput.IncrementTrigger>
        <ArkNumberInput.DecrementTrigger className={styles.numberTrigger}>
          <Icon name="chevron-down" size={16} />
        </ArkNumberInput.DecrementTrigger>
      </div>
    </ArkNumberInput.Control>
  </ArkNumberInput.Root>
);

NumberInput.displayName = 'NumberInput';
